/**
 * DIEUKYDIEU.TV - APPLE GENERIC CARD COMPONENT FACTORY (js/components/card.component.js)
 * Universal, Zero-Drift Card Component adhering to Apple HIG & Liquid Glass tokens (< 200 lines).
 */

/**
 * Tạo chuỗi HTML của Card (Dành cho nối chuỗi render danh sách tốc độ cao)
 * @param {Object} props - Tham số cấu hình thẻ
 * @returns {string} HTML string
 */
export function renderCardHTML(props = {}) {
    const {
        id = '',
        title = '',
        desc = '',
        img = '',
        variant = 'vertical', // 'vertical' | 'horizontal' | 'compact' | 'media-video' | 'media-audio' | 'media-gallery'
        kicker = '',
        badge = '',
        duration = '',
        artist = '',
        author = '',
        icon = '',
        staggerIndex = 0,
        legacyClass = '',
        actionLabel = '',
        onClickAttr = ''
    } = props;

    const isMusicOrAudio = (variant === 'media-music' || variant === 'media-audio');

    // Badge nổi trên ảnh: Đối với Âm nhạc & Audio, giữ mặt ảnh sạch sẽ 100% (chuẩn Apple Music / Spotify)
    let badgeHtml = '';
    if (badge) {
        badgeHtml = `<span class="apple-card-badge">${badge}</span>`;
    } else if (duration && variant === 'media-video') {
        badgeHtml = `<span class="apple-card-badge"><span class="material-symbols-rounded">schedule</span><span>${duration}</span></span>`;
    } else if (variant === 'media-gallery') {
        badgeHtml = `<span class="apple-card-badge"><span class="material-symbols-rounded">photo_library</span><span>Ảnh nét cao</span></span>`;
    }

    // Kicker tag nhỏ phía trên tiêu đề (bỏ với media-music/audio để giao diện thoáng, tinh tế)
    let kickerHtml = '';
    if (!isMusicOrAudio) {
        const kickerText = kicker || (variant === 'media-video' ? 'Video' : '');
        const kickerIcon = (variant === 'media-video') ? 'smart_display' : (variant === 'media-gallery' ? 'palette' : '');
        if (kickerText) {
            if (variant === 'editorial') {
                kickerHtml = `<div class="card-kicker-tag apple-card-kicker">${kickerText}</div>`;
            } else if (variant === 'related') {
                kickerHtml = `<div class="related-tag apple-card-kicker">${kickerText}</div>`;
            } else {
                kickerHtml = `<div class="apple-card-kicker">${kickerIcon ? `<span class="material-symbols-rounded">${kickerIcon}</span>` : '<span class="apple-card-kicker-dot"></span>'}<span>${kickerText}</span></div>`;
            }
        }
    }

    // Play overlay cho media video/audio/music
    const playOverlayHtml = (variant === 'media-video' || isMusicOrAudio)
        ? `<div class="apple-card-play-overlay"><span class="material-symbols-rounded apple-card-play-btn">play_arrow</span></div>`
        : '';

    // Subtitle hoặc Artist • Thời lượng
    let subtext = '';
    if (isMusicOrAudio) {
        const metaParts = [];
        if (artist || author) metaParts.push(artist || author);
        if (duration) metaParts.push(duration);
        subtext = metaParts.join(' • ');
    } else {
        subtext = artist ? `${artist}${desc ? ` — ${desc}` : ''}` : (author ? `${author}${desc ? ` — ${desc}` : ''}` : desc);
    }
    const subtextHtml = subtext ? `<p class="apple-card-desc card-desc">${subtext}</p>` : '';

    const clickAttr = onClickAttr ? ` onclick="${onClickAttr}"` : '';

    return `
        <article class="apple-card apple-card-${variant} ${legacyClass}" 
                 data-id="${id}" 
                 data-article-id="${id}"
                 tabindex="0" 
                 role="article" 
                 aria-label="${title}"
                 ${clickAttr}
                 style="--stagger-index: ${staggerIndex};">
            <div class="apple-card-thumb-wrap card-thumb-wrap">
                <div class="apple-card-thumb-bg thumb-bg" style="background-image: url('${img}')" loading="lazy"></div>
                ${badgeHtml}
                ${playOverlayHtml}
            </div>
            <div class="apple-card-content card-body">
                ${kickerHtml}
                <h3 class="apple-card-title card-heading" title="${title}">${title}</h3>
                ${subtextHtml}

                ${actionLabel ? `
                    <div class="apple-card-read-more related-read-more">
                        ${actionLabel} →
                    </div>
                ` : ''}
            </div>
        </article>
    `.trim();
}

/**
 * Tạo trực tiếp HTMLElement kèm Event Listener (Dành cho bind handler trực tiếp)
 */
export function createCardElement(props = {}) {
    const temp = document.createElement('div');
    temp.innerHTML = renderCardHTML(props);
    const cardEl = temp.firstElementChild;

    if (typeof props.onClick === 'function') {
        const handler = (e) => props.onClick(e, props);
        cardEl.addEventListener('click', handler);
        cardEl.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handler(e);
            }
        });
    }

    return cardEl;
}
